import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { BFLoader } from '@components/common/loader/main';
import Annotation from './annotation';
import Tooltip from './tooltip';
import NewAnnotation from './newAnnotation';

import './styles/annotations.scss';

const Annotator = ({
  annotationMode,
  annotations,
  onNewAnnotation,
  setTempAnnotation,
  tempAnnotation,
  url,
  isLoading,
}) => {
  const [hasMouseTrail, setHasMouseTrail] = useState(false);
  const [mouseTrailActive, setMouseTrailActive] = useState(false);
  const [imageIsLoading, setImageIsLoading] = useState(true);
  const sectionContainer = useRef(null);
  const imageContainer = useRef(null);
  const mouseTrail = useRef(null);

  useEffect(() => {
    if (annotationMode && hasMouseTrail && !tempAnnotation) {
      setMouseTrailActive(true);
    } else {
      setMouseTrailActive(false);
    }
  }, [annotationMode, annotations, hasMouseTrail, tempAnnotation]);

  // activate/deactivate mouse trail
  useEffect(() => {
    if (!imageContainer) { return undefined; }
    const imageEl = imageContainer.current;
    const onMouseLeave = () => setHasMouseTrail(false);
    imageEl.addEventListener('mouseleave', onMouseLeave);

    const onMouseMove = () => {
      if (mouseTrailActive) { return; }
      setHasMouseTrail(true);
    };
    imageEl.addEventListener('mouseenter', onMouseMove);

    return () => {
      imageEl.removeEventListener('mousemove', onMouseMove);
      imageEl.removeEventListener('mouseleave', onMouseLeave);
    };
  }, [imageContainer, mouseTrailActive]);

  // position mouse trail
  useEffect(() => {
    if (!sectionContainer || !mouseTrailActive) { return undefined; }
    const containerEl = sectionContainer.current;
    const imageEl = imageContainer.current;
    const mouseTrailEl = mouseTrail.current;

    const onMove = (e) => {
      if (e.target.classList.contains('annotator-image')) {
        const { offsetX, offsetY } = e;
        let x = offsetX + 20;
        let y = offsetY + 20;

        // spaces (gap) between image and container
        const leftGap = (containerEl.offsetWidth - imageEl.offsetWidth) / 2;
        const topGap = (containerEl.offsetHeight - imageEl.offsetHeight) / 2;

        const maxX = containerEl.offsetWidth - leftGap - mouseTrailEl.offsetWidth;
        if (x > maxX) { x = maxX; }

        const maxY = containerEl.offsetHeight - topGap - mouseTrailEl.offsetHeight;
        if (y > maxY) { y = maxY; }

        window.requestAnimationFrame(() => {
          mouseTrailEl.style.top = `${y}px`;
          mouseTrailEl.style.left = `${x}px`;
        });
      }
    };

    containerEl.addEventListener('mousemove', onMove);

    return () => {
      containerEl.removeEventListener('mousemove', onMove);
    };
  }, [sectionContainer, mouseTrailActive, mouseTrail]);

  const handleClick = (event) => {
    if (!annotationMode) { return undefined; }

    const { width, height } = event.target.getBoundingClientRect();
    const { offsetX: x, offsetY: y } = event.nativeEvent;

    const xPercent = x / width;
    const yPercent = y / height;

    setTempAnnotation({
      x: xPercent.toString(),
      y: yPercent.toString(),
      number: annotations.length + 1,
    });

    return undefined;
  };

  const onNewAnnotationSubmit = (text) => {
    onNewAnnotation({
      ...tempAnnotation,
      text,
    })
      .then(() => {
        setTempAnnotation(null);
      });
  };

  const annotationEls = annotations.map(({ x, y, id, comment }, i) => (
    <Annotation
      key={id}
      x={x}
      y={y}
      number={i + 1}
      onClick={() => { setTempAnnotation({ x, y, comment, number: i + 1 }); }}
    />
  ));

  return (
    <section
      className={`annotations annotation-mode-${annotationMode ? 'on' : 'off'}`}
      ref={sectionContainer}
    >
      {isLoading ? (
        <div className="loader-container">
          <BFLoader />
        </div>
      ) : (
        <div
          className="annotations-container"
          onClick={handleClick}
          role="button"
          tabIndex="0"
        >
          <img
            alt="attachment version"
            className="annotator-image"
            onLoad={() => setImageIsLoading(() => (false))}
            onError={() => setImageIsLoading(() => (false))}
            ref={imageContainer}
            src={url}
          />
          {imageIsLoading && <BFLoader />}
          {annotationMode && annotationEls}
          {tempAnnotation !== null && sectionContainer.current !== null && (
            <>
              <Annotation {...tempAnnotation} />
              <Tooltip
                x={tempAnnotation.x}
                y={tempAnnotation.y}
                containerWidth={sectionContainer.current.clientWidth}
                containerHeight={sectionContainer.current.clientHeight}
                onClose={() => setTempAnnotation(null)}
                setTempAnnotation={setTempAnnotation}
              >
                {tempAnnotation.comment
                  ? <p>{tempAnnotation.comment}</p>
                  : (
                    <NewAnnotation
                      onSubmit={onNewAnnotationSubmit}
                      number={tempAnnotation.number}
                    />
                  )
                }
              </Tooltip>
            </>
          )}
          {mouseTrailActive && (
            <div
              ref={mouseTrail}
              className="mouse-trail"
            >
              <p>{t('Click to leave an annotation')}</p>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

Annotator.propTypes = {
  annotationMode: PropTypes.bool,
  annotations: PropTypes.arrayOf(PropTypes.shape({
    x: PropTypes.string,
    y: PropTypes.string,
    id: PropTypes.string,
  })),
  onNewAnnotation: PropTypes.func,
  setTempAnnotation: PropTypes.func.isRequired,
  tempAnnotation: PropTypes.shape({
    x: PropTypes.string,
    y: PropTypes.string,
    comment: PropTypes.string,
    number: PropTypes.number,
  }),
  url: PropTypes.string.isRequired,
  isLoading: PropTypes.bool,
};

Annotator.defaultProps = {
  annotationMode: true,
  annotations: [],
  onNewAnnotation: undefined,
  tempAnnotation: null,
  isLoading: false,
};

export default Annotator;
